import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { fetchPageAssets } from '../../ducks/hostedAssets.duck';
import { denormalisedResponseEntities } from '../../util/data';
import { storableError } from '../../util/errors';
import { getServiceListingTypes } from '../../util/serviceSearch';
import { MAX_CARDS, heroFeedQueryParams, selectHeroSpecialists } from './heroSpecialists';

export const ASSET_NAME = 'landing-page';

// Hero feed (D40). The server waits at most this long; after it the page renders the hero photo.
export const HERO_FEED_SERVER_TIMEOUT = 1200;
// Routes calls loadData again after hydration: a feed this fresh is not fetched again, so the
// server-rendered feed (or the fallback photo) never changes after hydration.
export const HERO_FEED_FRESH_MS = 10 * 60 * 1000;

const isServer = () => typeof window === 'undefined';

const withTimeout = (promise, ms) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error('hero-feed-timeout')), ms);
    promise.then(
      value => {
        clearTimeout(timer);
        resolve(value);
      },
      e => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });

// ================ Async thunks ================ //

export const fetchHeroSpecialistsThunk = createAsyncThunk(
  'LandingPage/fetchHeroSpecialists',
  ({ config }, { extra: sdk, rejectWithValue }) => {
    const queryParams = heroFeedQueryParams(config);
    if (!queryParams) {
      return [];
    }
    const select = listings =>
      selectHeroSpecialists(listings, {
        serviceTypes: getServiceListingTypes(config),
        categoryConfiguration: config?.categoryConfiguration,
      });
    // Newest listings first, so a window with 12 eligible authors gives the exact feed. When the
    // first page does not (one author's many listings, ineligible authors), one more page, never
    // more; the server timeout covers both.
    const request = sdk.listings.query(queryParams).then(response => {
      const listings = denormalisedResponseEntities(response);
      const cards = select(listings);
      const hasMorePages = (response?.data?.meta?.totalPages || 1) > 1;
      return cards.length < MAX_CARDS && hasMorePages
        ? sdk.listings
            .query({ ...queryParams, page: 2 })
            .then(next => select([...listings, ...denormalisedResponseEntities(next)]))
        : cards;
    });
    return (isServer() ? withTimeout(request, HERO_FEED_SERVER_TIMEOUT) : request).catch(e =>
      rejectWithValue(storableError(e))
    );
  },
  {
    condition: (arg, { getState }) => {
      const { fetchInProgress, fetchedAt } = getState().LandingPage || {};
      const isFresh = !!fetchedAt && Date.now() - fetchedAt < HERO_FEED_FRESH_MS;
      return !fetchInProgress && !isFresh;
    },
  }
);

// Never rejects: a failed or slow feed only means the hero photo is shown.
export const fetchHeroSpecialists = config => dispatch =>
  dispatch(fetchHeroSpecialistsThunk({ config }));

// ================ Slice ================ //

const landingPageSlice = createSlice({
  name: 'LandingPage',
  initialState: {
    // null = not fetched yet; [] = nothing to show (or the fetch failed)
    heroSpecialists: null,
    fetchInProgress: false,
    fetchError: null,
    fetchedAt: null,
  },
  reducers: {},
  extraReducers: builder => {
    builder
      .addCase(fetchHeroSpecialistsThunk.pending, state => {
        state.fetchInProgress = true;
        state.fetchError = null;
      })
      .addCase(fetchHeroSpecialistsThunk.fulfilled, (state, action) => {
        state.fetchInProgress = false;
        state.heroSpecialists = action.payload;
        state.fetchedAt = Date.now();
      })
      .addCase(fetchHeroSpecialistsThunk.rejected, (state, action) => {
        state.fetchInProgress = false;
        state.fetchError = action.payload || null;
        // Keep a feed fetched earlier; without one the hero shows its photo
        state.heroSpecialists = state.heroSpecialists || [];
        state.fetchedAt = Date.now();
      });
  },
});

export default landingPageSlice.reducer;

// ================ Load data ================ //

export const loadData = (params, search, config) => dispatch => {
  const pageAsset = { landingPage: `content/pages/${ASSET_NAME}.json` };
  return Promise.all([
    dispatch(fetchPageAssets(pageAsset, true)),
    dispatch(fetchHeroSpecialists(config)),
  ]).then(([pageAssets]) => pageAssets);
};
